/**
 * Error Boundary Component
 * 
 * React Error Boundary that catches JavaScript errors anywhere in the child
 * component tree, logs those errors, and displays a fallback UI instead of
 * crashing the entire application. Integrates with error tracking service
 * (Sentry) if configured.
 * 
 * Usage:
 *   <ErrorBoundary fallback={<CustomErrorUI />}>
 *     <YourApp />
 *   </ErrorBoundary>
 */

import React, { Component } from 'react';
import { captureException } from '@/shared/utils/errorTracking.js';
import logger from '@/shared/utils/logger.js';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null, // Unique error ID for tracking
    };
    // Track online status for connectivity monitoring
    this.prevOnlineStatus = navigator.onLine;
  }

  static getDerivedStateFromError(error) {
    // If the user is offline, we don't want to show the full error boundary.
    // Instead, the OfflineBanner component will handle the visual feedback.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      // eslint-disable-next-line no-console
      console.warn('Suppressing ErrorBoundary due to offline status:', error);
      return null;
    }

    // Don't catch certain non-critical errors that can happen during updates or flaky network
    const nonCriticalErrors = [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Network request failed',
      'Failed to fetch',
      'AbortError',
      'ChunkLoadError',
      'Loading CSS chunk',
      'Loading chunk',
      'NetworkError',
      'TypeError: Failed to fetch',
      'TypeError: Network request failed',
      'ERR_INTERNET_DISCONNECTED',
      'ERR_NETWORK_CHANGED',
      'OFFLINE',
      'Failed to fetch dynamically imported module', // Vite specific
      'Importing a module script failed', // Browser specific
      'Script error', // Generic browser script error
      'Load failed' // Safari specific
    ];

    const errorMessage = error?.message || '';
    const errorName = error?.name || '';
    const isNonCritical = nonCriticalErrors.some(nonCriticalError =>
      errorMessage.includes(nonCriticalError) || errorName.includes(nonCriticalError)
    );

    // Only show error boundary for critical errors
    if (isNonCritical) {
      // eslint-disable-next-line no-console
      console.warn('Non-critical error caught by ErrorBoundary (not showing UI):', {
        message: errorMessage,
        name: errorName,
        error
      });
      return null; // Don't update state for non-critical errors
    }

    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Don't process non-critical errors that were filtered out in getDerivedStateFromError
    const errorMessage = error?.message || '';
    const errorName = error?.name || '';

    const nonCriticalErrors = [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Network request failed',
      'Failed to fetch',
      'AbortError',
      'ChunkLoadError',
      'Loading CSS chunk',
      'Loading chunk',
      'NetworkError',
      'TypeError: Failed to fetch',
      'TypeError: Network request failed',
      'ERR_INTERNET_DISCONNECTED',
      'ERR_NETWORK_CHANGED',
      'OFFLINE',
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      'Script error',
      'Load failed'
    ];

    const isNonCritical = nonCriticalErrors.some(nonCriticalError =>
      errorMessage.includes(nonCriticalError) || errorName.includes(nonCriticalError)
    );

    // Check if user is offline
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;

    if (isNonCritical || isOffline) {
      // Just log to console, don't show error UI or send to tracking
      // eslint-disable-next-line no-console
      console.info('Ignoring non-critical error or offline state in componentDidCatch:', {
        isOffline,
        errorMessage,
        errorName
      });
      return;
    }

    // Generate unique error ID for tracking
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Log error to console in development
    logger.error('Error Boundary caught an error', {
      errorId,
      error: {
        message: error?.message,
        stack: error?.stack,
      },
      errorInfo,
    });

    // Send to error tracking service (Sentry)
    captureException(error, {
      extra: {
        errorId,
        componentStack: errorInfo?.componentStack,
        errorBoundary: true,
      },
      tags: {
        errorBoundary: 'true',
        severity: 'high',
      },
    });

    // Store error ID in state for display
    this.setState({ errorId });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  // Add automatic recovery for certain errors
  componentDidMount() {
    // Listen for update-related errors and attempt recovery
    this.handleUpdateErrors = () => {
      // Clear any stale state that might cause issues during updates
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: null,
      });
    };

    // Handle network connectivity changes
    this.handleConnectivityChange = () => {
      const isOnline = navigator.onLine;

      if (isOnline && this.state.hasError) {
        // If we're back online and had an error, try to recover
        // eslint-disable-next-line no-console
        console.log('Network restored, attempting error recovery...');
        this.handleReset();
      } else if (!isOnline) {
        // If we go offline and have an error, clear it to avoid showing error screen
        // eslint-disable-next-line no-console
        console.log('Network lost, clearing error state...');
        this.setState({
          hasError: false,
          error: null,
          errorInfo: null,
          errorId: null,
        });
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', this.handleUpdateErrors);
    window.addEventListener('online', this.handleConnectivityChange);
    window.addEventListener('offline', this.handleConnectivityChange);

    // Set up auto-recovery timer for temporary errors
    this.autoRecoveryTimer = null;

    // Initial connectivity check
    if (!navigator.onLine) {
      // eslint-disable-next-line no-console
      console.log('App started in offline mode');
    }
  }

  componentWillUnmount() {
    // Clean up event listeners and timers
    if (this.handleUpdateErrors) {
      document.removeEventListener('visibilitychange', this.handleUpdateErrors);
    }
    if (this.handleConnectivityChange) {
      window.removeEventListener('online', this.handleConnectivityChange);
      window.removeEventListener('offline', this.handleConnectivityChange);
    }
    if (this.autoRecoveryTimer) {
      clearTimeout(this.autoRecoveryTimer);
    }
  }

  // Auto-recovery for temporary errors
  componentDidUpdate(prevProps, prevState) {
    // If we just entered an error state, set up auto-recovery
    if (this.state.hasError && !prevState.hasError) {
      // Check if it's a temporary error that might recover
      const errorMessage = this.state.error?.message || '';
      const errorName = this.state.error?.name || '';
      const temporaryErrors = [
        'Network request failed',
        'Failed to fetch',
        'AbortError',
        'ChunkLoadError',
        'NetworkError',
        'TypeError: Failed to fetch',
        'TypeError: Network request failed'
      ];

      const isTemporary = temporaryErrors.some(tempError =>
        errorMessage.includes(tempError) || errorName.includes(tempError)
      );

      if (isTemporary) {
        // Auto-retry after 5 seconds for temporary errors
        this.autoRecoveryTimer = setTimeout(() => {
          // eslint-disable-next-line no-console
          console.log('Attempting auto-recovery from temporary error...');
          this.handleReset();
        }, 5000);
      }
    }

    // Clear timer if error is resolved
    if (!this.state.hasError && prevState.hasError && this.autoRecoveryTimer) {
      clearTimeout(this.autoRecoveryTimer);
      this.autoRecoveryTimer = null;
    }

    // Handle offline state changes
    if (navigator.onLine !== this.prevOnlineStatus) {
      this.prevOnlineStatus = navigator.onLine;

      if (navigator.onLine && this.state.hasError) {
        // Network restored, try to recover from any error
        // eslint-disable-next-line no-console
        console.log('Network connection restored, attempting recovery...');
        this.handleReset();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '24px',
          background: 'var(--bg, #f5f7fb)',
          color: 'var(--text-primary, #0f172a)',
          fontFamily: '"Inter", sans-serif',
        }}>
          {/* Glass Card Container */}
          <div style={{
            maxWidth: '560px',
            width: '100%',
            backgroundColor: 'var(--card, #ffffff)',
            borderRadius: '24px',
            padding: '48px 40px',
            boxShadow: 'var(--shadow-xl, 0 20px 30px rgba(0, 0, 0, 0.15))',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid var(--border-secondary, #cbd5e1)',
          }}>
            {/* Top accent line */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '4px',
              background: 'linear-gradient(90deg, var(--error, #ef4444) 0%, #f87171 100%)',
            }} />

            {/* Error Icon Bubble */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: 'var(--error-light, #fef2f2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 32px',
              color: 'var(--error, #ef4444)',
              fontSize: '40px',
              boxShadow: '0 10px 20px rgba(239, 68, 68, 0.15)',
            }}>
              ⚠️
            </div>

            {/* Error Title */}
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              marginBottom: '16px',
              color: 'var(--text-primary, #0f172a)',
              letterSpacing: '-0.02em',
            }}>
              Something went wrong
            </h1>

            {/* Error Message */}
            <p style={{
              fontSize: '16px',
              marginBottom: '24px',
              color: 'var(--text-secondary, #475569)',
              lineHeight: '1.6',
              maxWidth: '400px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}>
              {(import.meta.env?.DEV || import.meta.env?.MODE === 'development')
                ? 'We encountered an unexpected error. See details below for debugging.'
                : 'We encountered an unexpected error. Our team has been notified and is working on a fix.'}
            </p>

            {/* Additional Help Text for Production */}
            {!(import.meta.env?.DEV || import.meta.env?.MODE === 'development') && (
              <p style={{
                fontSize: '14px',
                marginBottom: '32px',
                color: 'var(--text-muted, #64748b)',
                lineHeight: '1.5',
              }}>
                If this problem persists, please contact support or try refreshing the page.
              </p>
            )}

            {/* Error ID Badge */}
            {!(import.meta.env?.DEV || import.meta.env?.MODE === 'development') && this.state.errorId && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: 'var(--bg-tertiary, #f8fafc)',
                border: '1px solid var(--border-primary, #e2e8f0)',
                borderRadius: '99px',
                marginBottom: '40px',
                fontSize: '13px',
                color: 'var(--text-muted, #64748b)',
                fontFamily: 'monospace',
              }}>
                <span>Error ID:</span>
                <strong style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{this.state.errorId}</strong>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginTop: this.state.errorId ? '0' : '32px',
            }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: '14px 32px',
                  backgroundColor: 'var(--accent, #2563eb)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2), 0 2px 4px -1px rgba(37, 99, 235, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--accent-hover, #1d4ed8)';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 15px -3px rgba(37, 99, 235, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--accent, #2563eb)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(37, 99, 235, 0.2)';
                }}
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '14px 32px',
                  backgroundColor: 'transparent',
                  color: 'var(--text-primary, #0f172a)',
                  border: '1px solid var(--border-secondary, #cbd5e1)',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--bg-hover, #f1f5f9)';
                  e.target.style.borderColor = 'var(--text-muted, #94a3b8)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = 'var(--border-secondary, #cbd5e1)';
                }}
              >
                Reload Page
              </button>
            </div>

            {/* Error Details (Development Only) */}
            {(import.meta.env?.DEV || import.meta.env?.MODE === 'development') && this.state.error && (
              <div style={{ marginTop: '40px', borderTop: '1px solid var(--border-primary)', paddingTop: '32px' }}>
                <details style={{
                  padding: '16px',
                  backgroundColor: 'var(--bg-tertiary, #f8fafc)',
                  borderRadius: '12px',
                  textAlign: 'left',
                  border: '1px solid var(--border-primary, #e2e8f0)',
                }}>
                  <summary style={{
                    cursor: 'pointer',
                    fontWeight: '600',
                    marginBottom: '12px',
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span>View Debug Details</span>
                    {this.state.errorId && <span style={{ fontWeight: '400', color: 'var(--text-muted)' }}>(ID: {this.state.errorId})</span>}
                  </summary>
                  <pre style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: 'var(--text-secondary)',
                    fontFamily: '"Fira Code", monospace',
                    lineHeight: '1.5',
                  }}>
                    {(() => {
                      try {
                        const errorString = this.state.error?.toString?.() || String(this.state.error) || 'Unknown error';
                        const stackString = this.state.error?.stack || '';
                        return stackString ? `${errorString}\n\n${stackString}` : errorString;
                      } catch (err) {
                        return 'Error details could not be displayed due to serialization issues.';
                      }
                    })()}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;

